import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, CheckCircle, XCircle, CaretDown } from "@phosphor-icons/react";
import RescheduleMatchDialog from "@/components/tournament/RescheduleMatchDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Shows pending & recently-resolved player reschedule requests.
 * Admin can approve (opens the Reschedule dialog pre-set to the requested match)
 * or reject with an optional note.
 */
export default function RescheduleRequestsPanel({ onReschedule }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [reschedulingMatch, setReschedulingMatch] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/matches/reschedule-requests?status=pending`, { withCredentials: true });
      setRequests(res.data?.requests || []);
    } catch {
      setRequests([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const reject = async (req) => {
    const note = window.prompt(`Reject ${req.requester_name}'s request?\n(Optional: reason)`);
    if (note === null) return;
    try {
      await axios.post(`${API}/matches/reschedule-requests/${req.id}/resolve`,
        { action: "reject", note },
        { withCredentials: true });
      toast.success("Request rejected");
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reject");
    }
  };

  const approveAndReschedule = (req) => {
    if (!req.match) { toast.error("Match not found"); return; }
    setReschedulingMatch(req.match);
  };

  const pending = requests.filter(r => r.status === "pending");

  if (!pending.length && !loading) return null;

  return (
    <>
      <Card className="bg-card border-primary/30" data-testid="reschedule-requests-panel">
        <CardHeader className="cursor-pointer flex-row items-center justify-between pb-2" onClick={() => setExpanded(x => !x)}>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" weight="fill" />
            Player Reschedule Requests
            {pending.length > 0 && (
              <Badge className="bg-primary/20 text-primary border-primary/30" data-testid="reschedule-requests-count">
                {pending.length}
              </Badge>
            )}
          </CardTitle>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0">
            <CaretDown className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </Button>
        </CardHeader>

        {expanded && (
          <CardContent className="space-y-2">
            {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
            {!loading && pending.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-3">No pending requests</p>
            )}
            {pending.map(req => (
              <div key={req.id} className="p-3 rounded-lg border border-border bg-muted/20" data-testid={`request-row-${req.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-sm font-medium">
                      {req.requester_name}
                      <span className="text-muted-foreground font-normal ml-2">
                        · {req.match?.player1_name} vs {req.match?.player2_name}
                      </span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {req.tournament_name}
                      {req.match?.round && ` · ${req.match.round.replace(/_/g, " ")}`}
                    </p>
                    <p className="text-xs text-primary mt-0.5">
                      Original: {req.original_date || "TBD"}
                      {req.original_time ? ` at ${req.original_time}` : ""}
                      {req.original_table ? ` · ${req.original_table}` : ""}
                    </p>
                    {(req.preferred_date || req.preferred_time) && (
                      <p className="text-xs text-secondary mt-0.5">
                        Preferred: {req.preferred_date || "any"} {req.preferred_time ? `at ${req.preferred_time}` : ""}
                      </p>
                    )}
                    <p className="text-xs italic mt-1">"{req.reason}"</p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" className="border-chart-3/40 text-chart-3 h-7 text-xs"
                      onClick={() => approveAndReschedule(req)}
                      data-testid={`approve-request-${req.id}`}
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />Approve & Pick Slot
                    </Button>
                    <Button size="sm" variant="outline" className="border-destructive/40 text-destructive h-7 text-xs"
                      onClick={() => reject(req)}
                      data-testid={`reject-request-${req.id}`}
                    >
                      <XCircle className="w-3 h-3 mr-1" />Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      <RescheduleMatchDialog
        open={!!reschedulingMatch}
        onOpenChange={(v) => { if (!v) setReschedulingMatch(null); }}
        match={reschedulingMatch}
        tournamentId={reschedulingMatch?.tournament_id}
        onChanged={() => { fetch(); onReschedule?.(); }}
      />
    </>
  );
}
